import React, {useCallback, useEffect, useRef, useState} from 'react';
import {createPortal} from 'react-dom';
import CloseRounded from '@mui/icons-material/CloseRounded';
import AddRounded from '@mui/icons-material/AddRounded';
import RemoveRounded from '@mui/icons-material/RemoveRounded';
import RotateLeftRounded from '@mui/icons-material/RotateLeftRounded';
import RotateRightRounded from '@mui/icons-material/RotateRightRounded';
import FitScreenRounded from '@mui/icons-material/FitScreenRounded';
import DownloadRounded from '@mui/icons-material/DownloadRounded';
import ChevronLeftRounded from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRounded from '@mui/icons-material/ChevronRightRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import {imageSource, download} from './api';
import {boundView, fitImage, zoomAt} from './imageGeometry';
import {browserPreview} from '../home_media_ui/browserImagePreview';
import './imageViewer.css';

const resetView = () => ({zoom: 1, rotate: 0, x: 0, y: 0});
const distance = (a, b) => Math.hypot(a.x - b.x, a.y - b.y);
const midpoint = (a, b) => ({x: (a.x + b.x) / 2, y: (a.y + b.y) / 2});

export default function ImageViewer({images, startId, ctx, onClose, resolveSource, downloadFile}) {
  const [index, setIndex] = useState(() => Math.max(0, images.findIndex(image => image.id === startId)));
  const [source, setSource] = useState(null), [error, setError] = useState(''), [retry, setRetry] = useState(0);
  const [preparing, setPreparing] = useState('');
  const previewController = useRef(null), fallbackDispose = useRef(null), converting = useRef('');
  const [loadedKey, setLoadedKey] = useState(''), [saving, setSaving] = useState(false), [downloadError, setDownloadError] = useState('');
  const [view, setView] = useState(resetView), [natural, setNatural] = useState({width: 0, height: 0});
  const [viewport, setViewport] = useState({width: 0, height: 0}), [dragging, setDragging] = useState(false);
  const modal = useRef(null), stage = useRef(null), pointers = useRef(new Map()), gesture = useRef(null);
  const latest = useRef(null), closeRef = useRef(onClose), saveController = useRef(null);
  closeRef.current = onClose;
  latest.current = {view, natural, viewport};
  const current = images[index], currentKey = current ? `${current.id}:${current.version}` : '';
  const loaded = loadedKey === currentKey;
  const reset = useCallback(() => setView(resetView()), []);
  const go = useCallback(delta => {
    if (images.length < 2) return;
    setIndex(value => (value + delta + images.length) % images.length);
  }, [images.length]);
  const zoom = useCallback(factor => {
    const {natural: n, viewport: v} = latest.current;
    setView(value => zoomAt(value, value.zoom * factor, {x: 0, y: 0}, n, v));
  }, []);
  const rotate = useCallback(delta => {
    const {natural: n, viewport: v} = latest.current;
    setView(value => boundView({...value, rotate: (value.rotate + delta) % 360, x: 0, y: 0}, n, v));
  }, []);

  useEffect(() => {
    const controller = new AbortController(); previewController.current = controller;
    fallbackDispose.current?.(); fallbackDispose.current = null; converting.current = ''; setPreparing('');
    setError(''); setSource(null); setLoadedKey(''); setDownloadError('');
    setNatural({width: 0, height: 0}); reset(); pointers.current.clear(); gesture.current = null; setDragging(false);
    if (current) Promise.resolve().then(() => resolveSource
      ? resolveSource(current, controller.signal)
      : imageSource(current, ctx, {signal: controller.signal}))
      .then(url => {if (!controller.signal.aborted) setSource({key: currentKey, url, converted: false});})
      .catch(failure => {if (!controller.signal.aborted) setError(failure.message || 'Не удалось получить изображение.');});
    return () => {controller.abort(); fallbackDispose.current?.(); fallbackDispose.current = null;};
  }, [current, currentKey, ctx, retry, reset, resolveSource]);

  useEffect(() => {
    const measure = () => {
      const rect = stage.current?.getBoundingClientRect();
      if (rect) {setViewport({width: rect.width, height: rect.height}); setView(value => ({...value, x: 0, y: 0}));}
    };
    measure();
    const observer = window.ResizeObserver ? new window.ResizeObserver(measure) : null;
    if (observer && stage.current) observer.observe(stage.current);
    window.addEventListener('resize', measure);
    return () => {observer?.disconnect(); window.removeEventListener('resize', measure);};
  }, []);

  useEffect(() => {
    const before = document.activeElement, overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    modal.current?.querySelector('[data-close]')?.focus();
    const keydown = event => {
      if (event.key === 'Escape') {event.preventDefault(); event.stopPropagation(); closeRef.current(); return;}
      if (event.key === 'Tab') {
        const buttons = Array.from(modal.current?.querySelectorAll('button:not(:disabled)') || []);
        const first = buttons[0], last = buttons[buttons.length - 1];
        if (!modal.current?.contains(document.activeElement)) {event.preventDefault(); first?.focus();}
        else if (event.shiftKey && document.activeElement === first) {event.preventDefault(); last?.focus();}
        else if (!event.shiftKey && document.activeElement === last) {event.preventDefault(); first?.focus();}
        return;
      }
      if (event.key === 'ArrowLeft') {event.preventDefault(); go(-1);}
      if (event.key === 'ArrowRight') {event.preventDefault(); go(1);}
      if (['+', '='].includes(event.key)) {event.preventDefault(); zoom(1.25);}
      if (['-', '_'].includes(event.key)) {event.preventDefault(); zoom(0.8);}
      if (event.key === '0') {event.preventDefault(); reset();}
      if (['r', 'R', 'к', 'К'].includes(event.key)) {event.preventDefault(); rotate(event.shiftKey ? -90 : 90);}
    };
    document.addEventListener('keydown', keydown, true);
    return () => {
      document.removeEventListener('keydown', keydown, true); document.body.style.overflow = overflow;
      if (before?.isConnected) before.focus?.(); saveController.current?.abort();
    };
  }, [go, reset, zoom, rotate]);

  useEffect(() => {
    const element = stage.current;
    const wheel = event => {
      event.preventDefault();
      const rect = element.getBoundingClientRect(), point = {x: event.clientX - rect.left - rect.width / 2, y: event.clientY - rect.top - rect.height / 2};
      const {natural: n, viewport: v} = latest.current;
      setView(value => zoomAt(value, value.zoom * (event.deltaY < 0 ? 1.15 : 1 / 1.15), point, n, v));
    };
    element?.addEventListener('wheel', wheel, {passive: false});
    return () => element?.removeEventListener('wheel', wheel);
  }, []);

  const localPoint = point => {
    const rect = stage.current.getBoundingClientRect();
    return {x: point.x - rect.left - rect.width / 2, y: point.y - rect.top - rect.height / 2};
  };
  const beginGesture = () => {
    const points = Array.from(pointers.current.values()), initial = latest.current.view;
    gesture.current = points.length >= 2 ? {kind: 'pinch', view: initial, distance: Math.max(1, distance(points[0], points[1])),
      point: localPoint(midpoint(points[0], points[1]))} : points.length ? {kind: 'pan', view: initial, point: points[0]} : null;
  };
  const pointerDown = event => {
    if (!loaded || (event.pointerType === 'mouse' && event.button !== 0)) return;
    event.preventDefault();
    pointers.current.set(event.pointerId, {x: event.clientX, y: event.clientY});
    try {event.currentTarget.setPointerCapture(event.pointerId);} catch { /* Browser without capture. */ }
    beginGesture(); setDragging(true);
  };
  const pointerMove = event => {
    if (!pointers.current.has(event.pointerId) || !gesture.current) return;
    pointers.current.set(event.pointerId, {x: event.clientX, y: event.clientY});
    const points = Array.from(pointers.current.values()), start = gesture.current, {natural: n, viewport: v} = latest.current;
    if (start.kind === 'pinch' && points.length >= 2) {
      const center = localPoint(midpoint(points[0], points[1]));
      const next = zoomAt(start.view, start.view.zoom * distance(points[0], points[1]) / start.distance, start.point, n, v);
      setView(boundView({...next, x: next.x + center.x - start.point.x, y: next.y + center.y - start.point.y}, n, v));
    } else if (points.length === 1) setView(boundView({...start.view,
      x: start.view.x + points[0].x - start.point.x, y: start.view.y + points[0].y - start.point.y}, n, v));
  };
  const pointerUp = event => {
    pointers.current.delete(event.pointerId);
    try {event.currentTarget.releasePointerCapture(event.pointerId);} catch { /* Capture already released. */ }
    beginGesture(); if (!pointers.current.size) setDragging(false);
  };
  async function imageFailed() {
    const controller = previewController.current;
    if (!controller || controller.signal.aborted || !source || source.key !== currentKey) return;
    if (source.converted) {
      setError('Браузер не смог прочитать это изображение. Оригинал можно скачать. TIFF и редкие форматы могут требовать отдельного приложения.');
      return;
    }
    if (converting.current === currentKey) return;
    converting.current = currentKey; setPreparing('Подготавливаем изображение в вашем браузере…');
    try {
      const result = await browserPreview(current, source.url, controller.signal);
      if (controller.signal.aborted) {result.dispose(); return;}
      fallbackDispose.current?.(); fallbackDispose.current = result.dispose;
      setSource({key: currentKey, url: result.url, converted: true}); setPreparing('');
    } catch (failure) {
      if (!controller.signal.aborted) {setPreparing(''); setError(failure.message || 'Не удалось прочитать изображение. Скачайте оригинал.');}
    }
  }
  async function save() {
    if (!current || saving) return;
    setSaving(true); setDownloadError('');
    const controller = new AbortController(); saveController.current = controller;
    try {
      if (downloadFile) await downloadFile(current, controller.signal);
      else await download(current, ctx, ctx?.share ? undefined : current.version, controller.signal);
    }
    catch (failure) {if (!controller.signal.aborted) setDownloadError(failure.message);}
    finally {if (!controller.signal.aborted) setSaving(false);}
  }
  if (!current) return null;
  const fit = fitImage(natural, viewport, view.rotate);
  const button = (label, Icon, action, disabled = false) => <button type="button" title={label} aria-label={label} onClick={action} disabled={disabled}><Icon aria-hidden="true"/></button>;
  return createPortal(<section ref={modal} className="st-image-viewer" role="dialog" aria-modal="true" aria-label={`Просмотр: ${current.name}`}>
    <header className="st-image-header"><div><strong title={current.name}>{current.name}</strong><span>{index + 1} из {images.length}</span></div>
      <button data-close type="button" title="Закрыть (Esc)" aria-label="Закрыть просмотр" onClick={onClose}><CloseRounded/></button></header>
    <div ref={stage} className={`st-image-stage ${dragging ? 'is-grabbing' : ''}`} onPointerDown={pointerDown} onPointerMove={pointerMove}
      onPointerUp={pointerUp} onPointerCancel={pointerUp} onLostPointerCapture={event => {
        if (pointers.current.has(event.pointerId)) {pointers.current.delete(event.pointerId); beginGesture(); setDragging(false);}
      }} onDoubleClick={event => {if (event.target.tagName === 'IMG') {if (view.zoom > 1) reset(); else zoom(2);}}}>
      {!error && !loaded && <div className="st-image-status" role="status"><i className="st-spinner"/>{preparing || 'Загружаем изображение…'}</div>}
      {error && <div className="st-image-status st-image-error" role="alert"><strong>Не удалось открыть изображение</strong><p>{error}</p>
        <button onClick={() => setRetry(value => value + 1)}><RefreshRounded/>Повторить</button></div>}
      {source?.key === currentKey && !error && <img key={`${currentKey}:${retry}`} src={source.url} alt={current.name} draggable={false} referrerPolicy="no-referrer" crossOrigin="anonymous" decoding="async"
        onLoad={event => {setNatural({width: event.currentTarget.naturalWidth, height: event.currentTarget.naturalHeight}); setLoadedKey(currentKey);}}
        onError={imageFailed}
        style={{width: natural.width * fit || undefined, height: natural.height * fit || undefined, opacity: loaded ? 1 : 0,
          transform: `translate(${view.x}px, ${view.y}px) rotate(${view.rotate}deg) scale(${view.zoom})`}}/>}
      {images.length > 1 && <div className="st-image-navigation" onPointerDown={event => event.stopPropagation()}>
        {button('Предыдущее изображение (←)', ChevronLeftRounded, () => go(-1))}
        {button('Следующее изображение (→)', ChevronRightRounded, () => go(1))}
      </div>}
    </div>
    <footer className="st-image-footer">
      {downloadError && <p role="alert">{downloadError}</p>}
      <div className="st-image-tools">
        {button('Уменьшить (−)', RemoveRounded, () => zoom(0.8), !loaded || Boolean(error) || view.zoom <= 0.25)}
        <output title="Масштаб относительно размера по окну">{Math.round(view.zoom * 100)}%</output>
        {button('Увеличить (+)', AddRounded, () => zoom(1.25), !loaded || Boolean(error) || view.zoom >= 8)}
        <span className="st-image-divider"/>
        {button('Повернуть влево (Shift+R)', RotateLeftRounded, () => rotate(-90), !loaded || Boolean(error))}
        {button('Повернуть вправо (R)', RotateRightRounded, () => rotate(90), !loaded || Boolean(error))}
        {button('Вписать в окно (0)', FitScreenRounded, reset, !loaded || Boolean(error))}
        <span className="st-image-divider"/>
        {button(saving ? 'Получаем ссылку…' : 'Скачать оригинал', DownloadRounded, save, saving)}
      </div>
      <small>Колесо — масштаб · Потяните для перемещения · Два пальца — масштаб на телефоне</small>
    </footer>
  </section>, document.body);
}
